import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClock, faQuestion } from '@fortawesome/free-solid-svg-icons';

// Memoized so per-second ref-driven textContent mutations aren't overwritten
// when the parent re-renders for unrelated reasons (navigation, theme).
const TimerBadge = React.memo(({ timerTextRef, initialText, theme }) => {
  if (!initialText) return null;
  const style = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    borderRadius: '0.75rem',
    background: theme?.primary?.orange?.[500],
    color: 'white',
    fontWeight: '600',
    fontSize: '0.875rem',
    boxShadow: theme?.shadows?.sm,
  };
  return (
    <motion.div
      translate="no"
      style={style}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", damping: 15 }}
    >
      <FontAwesomeIcon icon={faClock} />
      <span ref={timerTextRef}>{initialText}</span>
    </motion.div>
  );
});

const TestProgress = ({
  currentIndex,
  totalQuestions,
  answeredQuestions,
  remainingTime,
  timerTextRef,
  theme
}) => {
  const progress = ((currentIndex + 1) / totalQuestions) * 100;
  const answeredProgress = (answeredQuestions / totalQuestions) * 100;

  const styles = {
    container: {
      marginBottom: '1.5rem',
      display: 'flex',
      justifyContent: 'flex-start', // changed from flex-end
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between', // changed from flex-start
      alignItems: 'center',
      width: '100%', // ensure it stretches
    },
    timerBadge: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginLeft: 'auto',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      borderRadius: '0.75rem',
      background: theme?.primary?.orange?.[500],
      color: 'white',
      fontWeight: '600',
      fontSize: '0.875rem',
      boxShadow: theme?.shadows?.sm,
    },
    questionInfo: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: theme?.primary?.blue?.[600],
    },
    statsGrid: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    statCard: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '0.75rem',
      padding: '0.5rem 1rem',
      marginLeft: '1rem',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '0.5rem',
    },
    statIcon: {
      fontSize: '.75rem',
      marginBottom: 0, // Remove bottom margin for inline
    },
    statValue: {
      fontSize: '1rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: 0,
    },
    statLabel: {
      fontSize: '0.75rem',
      color: theme?.text?.secondary,
    },
    progressSection: {
      marginBottom: '0.5rem',
    },
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      marginBottom: '0.5rem',
    },
    progressBarContainer: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '0.5rem',
      height: '0.5rem',
      overflow: 'hidden',
      position: 'relative',
    },
    progressBar: {
      height: '100%',
      borderRadius: '0.5rem',
      transition: 'width 0.5s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    progressShine: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
      animation: 'shine 2s infinite',
    },
  };

  const unansweredQuestions = totalQuestions - answeredQuestions;
  const remainingQuestions = totalQuestions - currentIndex - 1;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.questionInfo}>
          Question {currentIndex + 1} of {totalQuestions}
        </h2>
        
          <motion.div
            style={styles.statCard}
            whileHover={{ scale: 1.02 }}
          >
            {/* <FontAwesomeIcon 
              icon={faCheck} 
              style={{ 
                ...styles.statIcon,
                color: theme?.dashboard?.status?.correct,
              }} 
            /> */}
            <div style={styles.statValue}>{answeredQuestions}</div>
            <div style={styles.statLabel}>Answered</div>
          </motion.div>

          <motion.div
            style={styles.statCard}
            whileHover={{ scale: 1.02 }}
          >
            {/* <FontAwesomeIcon 
              icon={faQuestion} 
              style={{ 
                ...styles.statIcon,
                color: theme?.semantic?.warning,
              }} 
            /> */}
            <div style={styles.statValue}>{unansweredQuestions}</div>
            <div style={styles.statLabel}>Unanswered</div>
          </motion.div>
        
        <TimerBadge timerTextRef={timerTextRef} initialText={remainingTime} theme={theme} />
      </div>

      

      {/* <div style={styles.progressSection}>
        <div style={styles.progressLabel}>
          <span>Progress</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div style={styles.progressBarContainer}>
          <motion.div
            style={{ 
              ...styles.progressBar,
              background: `linear-gradient(90deg, ${theme?.primary?.teal?.[500]}, ${theme?.primary?.teal?.[600]})`,
              width: `${progress}%`,
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <div style={styles.progressShine} />
          </motion.div>
        </div>
      </div> */}

      {/* <div style={styles.progressSection}>
        <div style={styles.progressLabel}>
          <span>Completion</span>
          <span>{Math.round(answeredProgress)}%</span>
        </div>
        <div style={styles.progressBarContainer}>
          <motion.div
            style={{ 
              ...styles.progressBar,
              background: theme?.dashboard?.status?.correct,
              width: `${answeredProgress}%`,
            }}
            initial={{ width: 0 }}
            animate={{ width: `${answeredProgress}%` }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          />
        </div>
      </div> */}
    </div>
  );
};

export default TestProgress;